import { useEffect, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { usePwaInstall, openInAndroidBrowser } from "./usePwaInstall";
import { Button, CloseIcon, ModalSheet } from "@a2z/ui";
import {
  INSTALL_SHOWN_KEY,
  PWA_ENGAGEMENT_KEY,
  installMilestoneReached,
  isInstallCapSession,
} from "@/shared/pwa/lib/interruption-order";

// ---------------------------------------------------------------------------
// Throttle logic — max 2 shows per week, with at least 2 days between them
// ---------------------------------------------------------------------------

const MODAL_SHOW_KEY = INSTALL_SHOWN_KEY;
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

function getRecentTimestamps(): number[] {
  try {
    const raw = window.localStorage.getItem(MODAL_SHOW_KEY);
    if (!raw) return [];
    const timestamps = JSON.parse(raw) as number[];
    const cutoff = Date.now() - ONE_WEEK_MS;
    return timestamps.filter((t) => t > cutoff);
  } catch {
    return [];
  }
}

function canShowModal(): boolean {
  const recent = getRecentTimestamps();
  if (recent.length >= 2) return false;
  if (recent.length === 1) {
    const lastShown = Math.max(...recent);
    return Date.now() - lastShown >= TWO_DAYS_MS;
  }
  return true;
}

function recordModalShow(): void {
  try {
    const recent = getRecentTimestamps();
    recent.push(Date.now());
    window.localStorage.setItem(MODAL_SHOW_KEY, JSON.stringify(recent));
  } catch {
    // localStorage unavailable (private mode / quota) — skip persistence.
  }
}

// ---------------------------------------------------------------------------
// One interruption per session — install waits for a later visit
// ---------------------------------------------------------------------------

const FIRST_SESSION_KEY = "seller_first_session_v1";
const SESSION_MARKER_KEY = "seller_session_marker_v1";
/** Mirrors NotificationPromptBanner's session-only dismissal key. */
const NOTIFICATION_PROMPT_DISMISSED_KEY = "a2z_notification_prompt_dismissed_at";

/**
 * True from the seller's SECOND browser session on. The first session belongs
 * to the notifications prompt (the higher-priority ask): a new seller met the
 * notification sheet, the install sheet and a toast before seeing a screen.
 */
function isLaterVisit(): boolean {
  try {
    let marker = window.sessionStorage.getItem(SESSION_MARKER_KEY);
    if (!marker) {
      marker = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.sessionStorage.setItem(SESSION_MARKER_KEY, marker);
    }
    const first = window.localStorage.getItem(FIRST_SESSION_KEY);
    if (!first) {
      window.localStorage.setItem(FIRST_SESSION_KEY, marker);
      return false;
    }
    return first !== marker;
  } catch {
    return false;
  }
}

/**
 * The notifications prompt has priority and asks once per session while the
 * browser permission is undecided. Stand aside whenever it is showing, or has
 * already shown, this session.
 */
function notificationPromptOwnsSession(): boolean {
  try {
    if (window.sessionStorage.getItem(NOTIFICATION_PROMPT_DISMISSED_KEY))
      return true;
    return (
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    );
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Helper — open current URL in Safari on iOS
// ---------------------------------------------------------------------------

function openInSafari(): void {
  const url = window.location.href;
  // x-safari-https:// forces iOS to open the URL in Safari
  window.location.href = url.replace(/^https?:\/\//, (scheme) =>
    scheme === "https://" ? "x-safari-https://" : "x-safari-http://",
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InstallPrompt() {
  const t = useTranslations();
  const {
    canInstall,
    isInstalled,
    isIosSafari,
    isIosNonSafari,
    isAndroidManual,
    isInAppBrowser,
    isReady,
    install,
  } = usePwaInstall();
  // Engagement + weekly-throttle gate, sampled once per mount (lazy
  // initializer — the localStorage reads must not run during re-renders).
  const [autoOpenGateOk] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem(PWA_ENGAGEMENT_KEY) === "true" &&
      isLaterVisit() &&
      // Not before the first delivered order or the fifth session (B7).
      installMilestoneReached() &&
      // The notifications prompt owns the session while permission is
      // undecided — except the one session the install sheet claims from the
      // third on (shared/pwa/lib/interruption-order.ts).
      (isInstallCapSession() || !notificationPromptOwnsSession()) &&
      canShowModal(),
  );
  const [closedByUser, setClosedByUser] = useState(false);

  // Auto-open when conditions are met (mobile-only via CSS on the wrapper).
  // Derived during render instead of set from an effect; the render guards
  // below hide the whole subtree under the same eligibility conditions.
  const eligible =
    isReady &&
    !isInstalled &&
    (canInstall || isIosSafari || isIosNonSafari || isAndroidManual || isInAppBrowser);
  const isModalOpen = eligible && autoOpenGateOk && !closedByUser;

  // Persist the throttle timestamp once the modal actually shows.
  const recordedShowRef = useRef(false);
  useEffect(() => {
    if (isModalOpen && !recordedShowRef.current) {
      recordedShowRef.current = true;
      recordModalShow();
    }
  }, [isModalOpen]);

  const handleClose = () => {
    setClosedByUser(true);
  };

  // Focus, the Tab trap, Escape and the scrim are the docked sheet's
  // (`ModalSheet`), like every other sheet in the portal — this used to be a
  // hand-rolled floating card with its own trap.

  const handleInstall = async () => {
    await install();
    setClosedByUser(true);
  };

  if (!isReady || isInstalled) return null;
  if (!canInstall && !isIosSafari && !isIosNonSafari && !isAndroidManual && !isInAppBrowser)
    return null;

  const bodyText = isInAppBrowser
    ? t(keys.seller.pwaInAppBrowserBody)
    : isAndroidManual
      ? t(keys.seller.pwaAndroidManualBody)
      : isIosSafari
        ? t(keys.seller.pwaModalIosBody)
        : isIosNonSafari
          ? t(keys.seller.pwaModalIosNonSafariBody)
          : t(keys.seller.pwaModalBody);

  return (
    // Mobile-only wrapper — the modal uses fixed positioning so this just
    // acts as a guard; on desktop the whole subtree is hidden.
    <div className="lg:hidden">
      {/* Install sheet — the docked bottom sheet the share / more / customize
          sheets use: full width, flush with the screen's bottom edge, no gaps
          showing page rows beneath. */}
      <ModalSheet
        isOpen={isModalOpen}
        onClose={handleClose}
        size="sm"
        surface="card"
        labelledBy="pwa-install-title"
        data-testid="pwa-install-sheet"
      >
        <div className="px-5 pb-5 pt-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <h2
              id="pwa-install-title"
              className="text-lg font-semibold text-[color:var(--text-primary)]"
            >
              {t(keys.seller.pwaModalTitle)}
            </h2>
            {/* No focus dressing of its own: the platform's `:focus-visible`
                ring (tokens.css) shows on keyboard focus only. The sheet takes
                focus itself on open, so the close button no longer opens
                wearing the heavy 2 px ring the link-variant button drew. */}
            <button
              type="button"
              onClick={handleClose}
              aria-label={t(keys.seller.pwaInstallDismissAction)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--soft,var(--surface-alt))] text-[color:var(--text-secondary)] transition-colors hover:text-[color:var(--text-primary)]"
            >
              <CloseIcon size={18} />
            </button>
          </div>

          <p className="mb-6 text-sm leading-relaxed text-[color:var(--text-secondary)]">
            {bodyText}
          </p>

          <div className="flex gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleClose}
              className="flex-1"
            >
              {t(keys.seller.pwaInstallDismissAction)}
            </Button>
            {canInstall ? (
              <Button
                variant="primary"
                size="lg"
                onClick={() => void handleInstall()}
                className="flex-1"
              >
                {t(keys.seller.pwaInstallAction)}
              </Button>
            ) : isIosNonSafari ? (
              <Button
                variant="primary"
                size="lg"
                onClick={openInSafari}
                className="flex-1"
              >
                {t(keys.seller.pwaOpenInSafariAction)}
              </Button>
            ) : isInAppBrowser ? (
              <Button
                variant="primary"
                size="lg"
                onClick={openInAndroidBrowser}
                className="flex-1"
              >
                {t(keys.seller.pwaOpenInBrowserAction)}
              </Button>
            ) : null}
          </div>
        </div>
      </ModalSheet>

      {/* No follow-up after "later" (B7): it named a tab that does not
          exist, and a dismissal is an answer, not a cue for another message. */}
    </div>
  );
}
